import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCategory } from './CategoryContext';
import '../styles.css';
import { MenuItem, LocalCategoryModel } from '../models';

interface LocalCategory {
  id: string;
  name: string;
  items?: MenuItem[];
}

interface FeaturedCardsProps {
  language: 'en' | 'gr';
  categories?: LocalCategory[];
  fullCategories?: LocalCategoryModel[];
  activeCategory: string | null;
  onCategoryChange: (categoryID: string) => void;
}

const FeaturedCards: React.FC<FeaturedCardsProps> = ({
  language,
  categories = [],
  fullCategories = [],
  activeCategory,
  onCategoryChange,
}) => {
  const navigate = useNavigate();
  const { setSelectedCategory } = useCategory();
  const [featuredItems, setFeaturedItems] = useState<MenuItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(true);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const autoSlideRef = useRef<NodeJS.Timeout | null>(null);
  const [isSwiping, setIsSwiping] = useState(false);

  useEffect(() => {
    const featured = categories.flatMap((category) =>
      category.items?.filter((item: MenuItem) => item.featured && item.available) || []
    );
    setFeaturedItems(featured);
  }, [categories]);

  useEffect(() => {
    if (featuredItems.length > 0) {
      if (autoSlideRef.current) clearInterval(autoSlideRef.current);

      autoSlideRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % featuredItems.length);
      }, 4000);
    }
    return () => {
      if (autoSlideRef.current) clearInterval(autoSlideRef.current);
    };
  }, [featuredItems]);

  useEffect(() => {
    const timer = setTimeout(() => setShowWelcomeMessage(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  const getNoItemsMessage = () => (
    <div style={messageStyle}>
      {language === 'en' ? (
        <>
          Welcome to our Digital Menu
          <br />
          Enjoy your experience!
        </>
      ) : (
        <>
          Καλωσήρθατε στο
          <br />
          Ψηφιακό μας Μενού!
        </>
      )}
    </div>
  );

  const messageStyle: React.CSSProperties = {
    fontSize: 'clamp(1.5rem, 4vw, 2rem)',
    fontWeight: '600',
    textAlign: 'center',
    color: 'var(--color-primary, #005f73)',
    lineHeight: '1.6',
    padding: '3rem 1rem',
    background: 'linear-gradient(135deg, rgba(0, 95, 115, 0.03) 0%, rgba(0, 147, 177, 0.03) 100%)',
    borderRadius: '16px',
    fontFamily: "'Playfair Display', serif",
    letterSpacing: '-0.01em',
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = e.touches[0].clientX;
    setIsSwiping(false);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const swipeDistance = Math.abs(touchStartX.current - touchEndX.current);

    if (swipeDistance > 50) {
      setIsSwiping(true);
      if (touchStartX.current - touchEndX.current > 50) {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % featuredItems.length);
      } else if (touchEndX.current - touchStartX.current > 50) {
        setCurrentIndex((prevIndex) =>
          (prevIndex - 1 + featuredItems.length) % featuredItems.length
        );
      }
    }
  };

  const renderDots = () => (
    <div className="dots-container" role="tablist" aria-label="Carousel navigation">
      {featuredItems.map((_, index) => (
        <span
          key={index}
          className={`dot ${index === currentIndex ? 'active' : ''}`}
          onClick={() => setCurrentIndex(index)}
          role="tab"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setCurrentIndex(index); }}
          aria-label={language === 'en' ? `Slide ${index + 1}` : `Διαφάνεια ${index + 1}`}
          aria-selected={index === currentIndex}
        />
      ))}
    </div>
  );

  const handleItemClick = (item: MenuItem, event: React.MouseEvent | React.TouchEvent) => {
    event.stopPropagation();

    if (isSwiping) {
      return;
    }

    const category = fullCategories.find(cat => cat.categoryID === item.categoryID);

    if (category) {
      setSelectedCategory(category);
      navigate(`/items/${category.categoryID}`);
    }
  };

  return (
    <div className="featured-cards">
      {showWelcomeMessage ? (
        <div>{getNoItemsMessage()}</div>
      ) : (
        <div className={`carousel-wrapper fade-in`}>
          {featuredItems.length > 0 ? (
            <div
              className="carousel-inner"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {featuredItems.map((item, index) => (
                <div
                  key={item.itemID || `${item.itemOrder}-${index}`}
                  className="card"
                  onClick={(e) => handleItemClick(item, e)}
                  style={{ cursor: 'pointer' }}
                >
                  <div
                    className="card-content"
                    style={{ backgroundImage: `url(${encodeURI(item.img || '')})` }}
                  >
                    <div className="card-info">
                      <h3>{item.itemTitle}</h3>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            !categories.length && <div>{getNoItemsMessage()}</div>
          )}
        </div>
      )}
      {!showWelcomeMessage && featuredItems.length > 0 && renderDots()}
    </div>
  );
};

export default FeaturedCards;
